import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import ExperimentHub from './pages/ExperimentHub';
import Backlog from './pages/Backlog';
import MarketInsights from './pages/MarketInsights';
import ProductDashboard from './pages/ProductDashboard';
import TechnologyDashboard from './pages/TechnologyDashboard';
import FinancialDashboard from './pages/FinancialDashboard';
import MarketingDashboard from './pages/MarketingDashboard';
import DataDashboard from './pages/DataDashboard';
import EngineeringDashboard from './pages/EngineeringDashboard';
import IntegrationsPage from './pages/IntegrationsPage';
import IntegrationForm from './pages/IntegrationForm';
import IntegrationDetails from './pages/IntegrationDetails';
import AuthPage from './components/auth/AuthPage';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import Pricing from './pages/Pricing';
import SuccessPage from './pages/SuccessPage';
import AccountPage from './pages/AccountPage';
import CustomerDashboard from './pages/CustomerDashboard';
import Terms from './pages/Terms';

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Auth routes */}
          <Route path="/login" element={<AuthPage />} />
          <Route path="/signup" element={<AuthPage />} />
          
          {/* Public routes */}
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/terms" element={<Terms />} />
          
          {/* Protected routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/experiments" element={
            <ProtectedRoute>
              <Layout>
                <ExperimentHub />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/backlog" element={
            <ProtectedRoute>
              <Layout>
                <Backlog />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/market-insights" element={
            <ProtectedRoute>
              <Layout>
                <MarketInsights />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/account" element={
            <ProtectedRoute>
              <Layout>
                <AccountPage />
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* Integrations routes */}
          <Route path="/integrations" element={
            <ProtectedRoute>
              <Layout>
                <IntegrationsPage />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/integrations/new" element={
            <ProtectedRoute>
              <Layout>
                <IntegrationForm />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/integrations/:id" element={
            <ProtectedRoute>
              <Layout>
                <IntegrationDetails />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/integrations/edit/:id" element={
            <ProtectedRoute>
              <Layout>
                <IntegrationForm />
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* Role-specific dashboards - Main implementations */}
          <Route path="/product-dashboard" element={
            <ProtectedRoute>
              <Layout>
                <ProductDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/technology-dashboard" element={
            <ProtectedRoute>
              <Layout>
                <TechnologyDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/financial-dashboard" element={
            <ProtectedRoute>
              <Layout>
                <FinancialDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/marketing-dashboard" element={
            <ProtectedRoute>
              <Layout>
                <MarketingDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/data-dashboard" element={
            <ProtectedRoute>
              <Layout>
                <DataDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/engineering-dashboard" element={
            <ProtectedRoute>
              <Layout>
                <EngineeringDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/customer-dashboard" element={
            <ProtectedRoute>
              <Layout>
                <CustomerDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* Additional role-specific dashboards - Placeholder implementations */}
          <Route path="/system-health" element={
            <ProtectedRoute>
              <Layout>
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white">System Health Dashboard</h1>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
                    <h2 className="text-xl font-semibold text-white mb-4">Real-time System Monitoring</h2>
                    <p className="text-gray-400 mb-6">Infrastructure health, performance metrics, and system reliability monitoring.</p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">System Uptime</h3>
                        <div className="text-3xl font-bold text-green-400">99.9%</div>
                        <div className="text-sm text-gray-400">Last 30 days</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Response Time</h3>
                        <div className="text-3xl font-bold text-blue-400">85ms</div>
                        <div className="text-sm text-gray-400">Average</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Error Rate</h3>
                        <div className="text-3xl font-bold text-yellow-400">0.02%</div>
                        <div className="text-sm text-gray-400">Last 24 hours</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Active Services</h3>
                        <div className="text-3xl font-bold text-purple-400">24/24</div>
                        <div className="text-sm text-gray-400">All healthy</div>
                      </div>
                    </div>
                    <div className="mt-6 text-center">
                      <p className="text-indigo-400">Full dashboard implementation coming soon...</p>
                    </div>
                  </div>
                </div>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/developer-experience" element={
            <ProtectedRoute>
              <Layout>
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white">Developer Experience Dashboard</h1>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
                    <h2 className="text-xl font-semibold text-white mb-4">Development Velocity & Team Productivity</h2>
                    <p className="text-gray-400 mb-6">Developer satisfaction, build performance, and development workflow metrics.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Developer Satisfaction</h3>
                        <div className="text-3xl font-bold text-green-400">8.2/10</div>
                        <div className="text-sm text-gray-400">+0.5 vs last quarter</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Build Success Rate</h3>
                        <div className="text-3xl font-bold text-blue-400">94%</div>
                        <div className="text-sm text-gray-400">+2.1% improvement</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Code Review Time</h3>
                        <div className="text-3xl font-bold text-purple-400">4.2h</div>
                        <div className="text-sm text-gray-400">-1.3h improvement</div>
                      </div>
                    </div>
                    <div className="mt-6 text-center">
                      <p className="text-indigo-400">Full dashboard implementation coming soon...</p>
                    </div>
                  </div>
                </div>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/roi-analysis" element={
            <ProtectedRoute>
              <Layout>
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white">ROI Analysis Dashboard</h1>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
                    <h2 className="text-xl font-semibold text-white mb-4">Return on Investment Analysis</h2>
                    <p className="text-gray-400 mb-6">Detailed ROI analysis and projections across all business initiatives.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Portfolio ROI</h3>
                        <div className="text-3xl font-bold text-green-400">320%</div>
                        <div className="text-sm text-gray-400">+12.3% vs target</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Investment Efficiency</h3>
                        <div className="text-3xl font-bold text-blue-400">94%</div>
                        <div className="text-sm text-gray-400">Capital productivity</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Payback Period</h3>
                        <div className="text-3xl font-bold text-purple-400">8.2</div>
                        <div className="text-sm text-gray-400">months average</div>
                      </div>
                    </div>
                    <div className="mt-6 text-center">
                      <p className="text-indigo-400">Full dashboard implementation coming soon...</p>
                    </div>
                  </div>
                </div>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/cost-optimization" element={
            <ProtectedRoute>
              <Layout>
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-yellow-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white">Cost Optimization Dashboard</h1>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
                    <h2 className="text-xl font-semibold text-white mb-4">Cost Analysis & Optimization</h2>
                    <p className="text-gray-400 mb-6">Cost analysis and optimization opportunities across all business areas.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Cost Savings</h3>
                        <div className="text-3xl font-bold text-green-400">£2.4M</div>
                        <div className="text-sm text-gray-400">This quarter</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Efficiency Gain</h3>
                        <div className="text-3xl font-bold text-blue-400">22%</div>
                        <div className="text-sm text-gray-400">Process optimization</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Budget Variance</h3>
                        <div className="text-3xl font-bold text-yellow-400">-8%</div>
                        <div className="text-sm text-gray-400">Under budget</div>
                      </div>
                    </div>
                    <div className="mt-6 text-center">
                      <p className="text-indigo-400">Full dashboard implementation coming soon...</p>
                    </div>
                  </div>
                </div>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/brand-health" element={
            <ProtectedRoute>
              <Layout>
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-pink-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white">Brand Health Dashboard</h1>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
                    <h2 className="text-xl font-semibold text-white mb-4">Brand Perception & Market Positioning</h2>
                    <p className="text-gray-400 mb-6">Brand perception analysis and market positioning metrics.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Brand Awareness</h3>
                        <div className="text-3xl font-bold text-green-400">68%</div>
                        <div className="text-sm text-gray-400">+5.2% vs last quarter</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Brand Sentiment</h3>
                        <div className="text-3xl font-bold text-blue-400">4.6/5</div>
                        <div className="text-sm text-gray-400">Positive sentiment</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Share of Voice</h3>
                        <div className="text-3xl font-bold text-purple-400">24%</div>
                        <div className="text-sm text-gray-400">Market category</div>
                      </div>
                    </div>
                    <div className="mt-6 text-center">
                      <p className="text-indigo-400">Full dashboard implementation coming soon...</p>
                    </div>
                  </div>
                </div>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/model-performance" element={
            <ProtectedRoute>
              <Layout>
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white">Model Performance Dashboard</h1>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
                    <h2 className="text-xl font-semibold text-white mb-4">Machine Learning Model Monitoring</h2>
                    <p className="text-gray-400 mb-6">Machine learning model monitoring and performance tracking.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Model Accuracy</h3>
                        <div className="text-3xl font-bold text-green-400">91.2%</div>
                        <div className="text-sm text-gray-400">Average across models</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Prediction Latency</h3>
                        <div className="text-3xl font-bold text-blue-400">45ms</div>
                        <div className="text-sm text-gray-400">Average response time</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Model Drift</h3>
                        <div className="text-3xl font-bold text-yellow-400">2.8%</div>
                        <div className="text-sm text-gray-400">Within acceptable range</div>
                      </div>
                    </div>
                    <div className="mt-6 text-center">
                      <p className="text-indigo-400">Full dashboard implementation coming soon...</p>
                    </div>
                  </div>
                </div>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/insight-tracking" element={
            <ProtectedRoute>
              <Layout>
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-cyan-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white">Insight Tracking Dashboard</h1>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
                    <h2 className="text-xl font-semibold text-white mb-4">Data Insights Generation & Impact</h2>
                    <p className="text-gray-400 mb-6">Data insights generation and impact tracking across the organization.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Insights Generated</h3>
                        <div className="text-3xl font-bold text-green-400">67</div>
                        <div className="text-sm text-gray-400">This month</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Insights Acted Upon</h3>
                        <div className="text-3xl font-bold text-blue-400">35</div>
                        <div className="text-sm text-gray-400">52% action rate</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Business Impact</h3>
                        <div className="text-3xl font-bold text-purple-400">£2.8M</div>
                        <div className="text-sm text-gray-400">Value generated</div>
                      </div>
                    </div>
                    <div className="mt-6 text-center">
                      <p className="text-indigo-400">Full dashboard implementation coming soon...</p>
                    </div>
                  </div>
                </div>
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* Admin routes */}
          <Route path="/user-management" element={
            <ProtectedRoute>
              <Layout>
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                      </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white">User Management</h1>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
                    <h2 className="text-xl font-semibold text-white mb-4">User Administration & Access Control</h2>
                    <p className="text-gray-400 mb-6">Manage users, roles, and access permissions across the platform.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Total Users</h3>
                        <div className="text-3xl font-bold text-blue-400">1,247</div>
                        <div className="text-sm text-gray-400">Active accounts</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Active Sessions</h3>
                        <div className="text-3xl font-bold text-green-400">892</div>
                        <div className="text-sm text-gray-400">Currently online</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Role Types</h3>
                        <div className="text-3xl font-bold text-purple-400">8</div>
                        <div className="text-sm text-gray-400">Configured roles</div>
                      </div>
                    </div>
                    <div className="mt-6 text-center">
                      <p className="text-indigo-400">Full user management interface coming soon...</p>
                    </div>
                  </div>
                </div>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/role-permissions" element={
            <ProtectedRoute>
              <Layout>
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white">Role Permissions</h1>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
                    <h2 className="text-xl font-semibold text-white mb-4">Role-Based Access Control</h2>
                    <p className="text-gray-400 mb-6">Configure role-based access control and permission management.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Permission Groups</h3>
                        <div className="text-3xl font-bold text-red-400">24</div>
                        <div className="text-sm text-gray-400">Configured groups</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Access Policies</h3>
                        <div className="text-3xl font-bold text-yellow-400">156</div>
                        <div className="text-sm text-gray-400">Active policies</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Security Level</h3>
                        <div className="text-3xl font-bold text-green-400">High</div>
                        <div className="text-sm text-gray-400">Compliance ready</div>
                      </div>
                    </div>
                    <div className="mt-6 text-center">
                      <p className="text-indigo-400">Full permission management interface coming soon...</p>
                    </div>
                  </div>
                </div>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/system-settings" element={
            <ProtectedRoute>
              <Layout>
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white">System Settings</h1>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
                    <h2 className="text-xl font-semibold text-white mb-4">System Configuration & Preferences</h2>
                    <p className="text-gray-400 mb-6">Configure system-wide settings and preferences.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Configuration Items</h3>
                        <div className="text-3xl font-bold text-gray-400">47</div>
                        <div className="text-sm text-gray-400">System settings</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Environment</h3>
                        <div className="text-3xl font-bold text-blue-400">Prod</div>
                        <div className="text-sm text-gray-400">Current environment</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-2">Last Backup</h3>
                        <div className="text-3xl font-bold text-green-400">2h</div>
                        <div className="text-sm text-gray-400">ago</div>
                      </div>
                    </div>
                    <div className="mt-6 text-center">
                      <p className="text-indigo-400">Full system settings interface coming soon...</p>
                    </div>
                  </div>
                </div>
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* Catch-all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;